import React, { useEffect, useState } from 'react';
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import './Preco.css';
import { useAuth } from '../Contexts/authContext';

const Preco = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [paypalConfig, setPaypalConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, updateUser, isAuthenticated } = useAuth();
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [paymentGateway, setPaymentGateway] = useState('paypal');
  const [stripeConfig, setStripeConfig] = useState(null);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        // Get active payment gateway config
        const stripeRes = await axios.get('/api/stripe/config');
        setPaymentGateway(stripeRes.data.gateway);
        setStripeConfig(stripeRes.data);

        // Get PayPal config
        const response = await axios.get('/api/paypal/config');
        setPaypalConfig(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error loading config:', err);
        setError(t('pages.pricing.errorConfig'));
        setLoading(false);
      }
    };

    fetchConfig();
  }, [t]);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getPayPalLocale = (language) => {
    const locales = {
      'pt': 'pt_PT',
      'es': 'es_ES',
      'fr': 'fr_FR',
      'it': 'it_IT',
      'de': 'de_DE',
      'en': 'en_US'
    };
    return locales[language] || 'en_US';
  };

  const handleApprove = async (data, actions) => {
    console.log('[PayPal] Subscription Approved:', data);

    try {
      const verifyResponse = await axios.post('/api/paypal/verify-subscription', {
        subscriptionId: data.subscriptionID,
        email: user?.email
      });

      console.log('[PayPal] Backend Verification:', verifyResponse.data);

      if (verifyResponse.data.success) {
        if (verifyResponse.data.user && updateUser) {
          console.log('[PayPal] Updating local user context:', verifyResponse.data.user);
          updateUser(verifyResponse.data.user);
        }

        console.log('[PayPal] Redirecting to Thank You page...');
        setTimeout(() => {
          window.location.href = `/${i18n.language}/thank-you`;
        }, 1000);
      }
    } catch (e) {
      console.error('[PayPal] Verification failed', e);
      setError(t('pages.pricing.errorVerify'));
    }
  };

  const handleSkip = async () => {
    try {
      if (!user?.email) {
        // Fallback se o usuário não estiver no contexto mas estiver logado (raro)
        console.error('User email not found in context');
        return;
      }

      setLoading(true);
      const response = await axios.post('/api/auth/set-free-plan', { email: user.email });

      if (response.data.user && updateUser) {
        updateUser(response.data.user);
      }

      // Redireciona para o dashboard
      navigate(`/${i18n.language}/club-sheet`);
    } catch (err) {
      console.error('Error setting free plan:', err);
      setError('Erro ao ativar modo gratuito.');
      setLoading(false);
    }
  };

  const handleStripeCheckout = async () => {
    try {
      if (!user?.email) {
        console.error('User email not found in context');
        return;
      }
      setLoading(true);
      const response = await axios.post('/api/stripe/create-checkout-session', {
        email: user.email,
        lang: i18n.language
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No checkout URL returned from backend');
      }
    } catch (err) {
      console.error('[STRIPE] Checkout failed:', err);
      setError(t('pages.pricing.errorStripe') || 'Erro ao iniciar o pagamento com Stripe. Por favor, tente novamente.');
      setLoading(false);
    }
  };



  // Se o utilizador já tem subscrição ativa, mostrar mensagem em vez do pagamento
  if (user && user.subscriptionStatus === 'active') {
    return (
      <div className="preco-container">
        <SEO titleKey="seo.pricing.title" descriptionKey="seo.pricing.description" />
        <div className="preco-content">
          <div className="plans-container single-plan">
            <div className="plan-card basic" style={{ textAlign: 'center', padding: '40px 30px' }}>
              <div style={{ fontSize: '60px', marginBottom: '20px' }}>✅</div>
              <h2 style={{ color: '#22c55e', marginBottom: '12px' }}>
                {t('pages.pricing.alreadyActive') || 'Subscrição Ativa!'}
              </h2>
              <p style={{ color: '#666', marginBottom: '30px' }}>
                {t('pages.pricing.alreadyActiveDesc') || 'A tua conta já tem uma subscrição ativa. Podes continuar a utilizar todas as funcionalidades premium.'}
              </p>
              <button
                className="cta-button"
                style={{ display: 'block', width: '100%', maxWidth: '280px', margin: '0 auto', padding: '14px 24px', fontSize: '16px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                onClick={() => navigate(`/${i18n.language}/club-sheet`)}
              >
                {t('pages.pricing.goToDashboard') || 'Ir para o Dashboard'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="preco-container">
      <SEO titleKey="seo.pricing.title" descriptionKey="seo.pricing.description" />
      <div className="preco-content">
        <h1>{t('pages.pricing.title')}</h1>
        <p>{t('pages.pricing.subtitle')}</p>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {loading ? (
          <div className="loading-message">{t('pages.pricing.loading')}</div>
        ) : (
          /* Se o utilizador não estiver autenticado, mostra botão para registar primeiro */
          !user ? (
            <div className="plans-container single-plan">
              <div className="plan-card basic">
                <div className="plan-header">
                  <h3>{t('pages.pricing.planTitle')}</h3>
                  <div className="plan-price">
                    <span className="currency">{t('pages.pricing.currency')}</span>
                    <span className="amount">19,99</span>
                    <span className="period">{t('pages.pricing.period')}</span>
                  </div>
                </div>
                <ul className="plan-features">
                  <li>{t('pages.pricing.feature1')}</li>
                  <li>{t('pages.pricing.feature2')}</li>
                  <li>{t('pages.pricing.feature3')}</li>
                  <li>{t('pages.pricing.feature4')}</li>
                </ul>

                <div className="login-prompt-container" style={{ textAlign: 'center', marginTop: '20px' }}>
                  <button
                    className="cta-button"
                    style={{ width: '100%', padding: '12px', fontSize: '16px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    onClick={() => navigate(`/${i18n.language}/register`, { state: { returnUrl: `/${i18n.language}/pricing` } })}
                  >
                    {t('auth.registerTitle')} {t('common.to')} {t('pages.pricing.subscribe')}
                  </button>
                  <p style={{ marginTop: '10px', fontSize: '14px' }}>
                    {t('auth.alreadyHaveAccount')} <span
                      style={{ color: '#2563eb', cursor: 'pointer', textDecoration: 'underline' }}
                      onClick={() => navigate(`/${i18n.language}/login`, { state: { returnUrl: `/${i18n.language}/pricing` } })}
                    >{t('auth.login')}</span>
                  </p>
                </div>
              </div>
            </div>
          ) : (
            paymentGateway === 'stripe' ? (
              <div className="plans-container single-plan">
                <div className="plan-card basic">
                  <div className="plan-header">
                    <h3>{t('pages.pricing.planTitle')}</h3>
                    <div className="plan-price">
                      <span className="currency">{t('pages.pricing.currency')}</span>
                      <span className="amount">19,99</span>
                      <span className="period">{t('pages.pricing.period')}</span>
                    </div>
                  </div>
                  <ul className="plan-features">
                    <li>{t('pages.pricing.feature1')}</li>
                    <li>{t('pages.pricing.feature2')}</li>
                    <li>{t('pages.pricing.feature3')}</li>
                    <li>{t('pages.pricing.feature4')}</li>
                  </ul>

                  <div className="stripe-button-container" style={{ marginTop: '30px' }}>
                    <button
                      className="cta-button"
                      style={{ 
                        width: '100%', 
                        padding: '14px 24px', 
                        fontSize: '18px', 
                        backgroundColor: '#635bff', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '8px', 
                        cursor: 'pointer',
                        fontWeight: '600',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '10px',
                        boxShadow: '0 4px 12px rgba(99, 91, 255, 0.2)',
                        transition: 'all 0.2s ease'
                      }}
                      onClick={handleStripeCheckout}
                    >
                      💳 {t('pages.pricing.payWithStripe') || 'Pay with Card / Google Pay'}
                    </button>
                  </div>

                  <div className="skip-container" style={{ marginTop: '20px', textAlign: 'center' }}>
                    <button
                      onClick={handleSkip}
                      className="skip-button"
                      style={{
                        background: 'transparent',
                        border: '1px solid #ccc',
                        padding: '10px 20px',
                        cursor: 'pointer',
                        color: '#666',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    >
                      {t('pages.pricing.skipButton')}
                    </button>
                  </div>

                </div>
              </div>
            ) : (
              paypalConfig && (
                <PayPalScriptProvider
                  options={{
                    "client-id": paypalConfig.clientId,
                    currency: "EUR",
                    intent: "subscription",
                    vault: true,
                    locale: getPayPalLocale(i18n.language)
                  }}
                >
                  <div className="plans-container single-plan">
                    <div className="plan-card basic">
                      <div className="plan-header">
                        <h3>{t('pages.pricing.planTitle')}</h3>
                        <div className="plan-price">
                          <span className="currency">{t('pages.pricing.currency')}</span>
                          <span className="amount">19,99</span>
                          <span className="period">{t('pages.pricing.period')}</span>
                        </div>
                      </div>
                      <ul className="plan-features">
                        <li>{t('pages.pricing.feature1')}</li>
                        <li>{t('pages.pricing.feature2')}</li>
                        <li>{t('pages.pricing.feature3')}</li>
                        <li>{t('pages.pricing.feature4')}</li>
                      </ul>

                      <div className="paypal-button-container">
                        <div className="payment-separator">
                          <span>{t('pages.pricing.payWith')}</span>
                        </div>

                        {/* Google Pay Button */}
                        <div className="button-wrapper google-pay-wrapper">
                          <PayPalButtons
                            fundingSource="googlepay"
                            createSubscription={(data, actions) => {
                              return actions.subscription.create({
                                plan_id: paypalConfig.planId
                              });
                            }}
                            onApprove={handleApprove}
                            onError={(err) => console.error('[PayPal] GooglePay Error:', err)}
                            style={{
                              shape: 'rect',
                              color: 'black',
                              layout: 'horizontal',
                              label: 'pay',
                              height: 45
                            }}
                          />
                        </div>

                        {/* Apple Pay Button */}
                        <div className="button-wrapper apple-pay-wrapper">
                          <PayPalButtons
                            fundingSource="applepay"
                            createSubscription={(data, actions) => {
                              return actions.subscription.create({
                                plan_id: paypalConfig.planId
                              });
                            }}
                            onApprove={handleApprove}
                            onError={(err) => console.error('[PayPal] ApplePay Error:', err)}
                            style={{
                              shape: 'rect',
                              color: 'black',
                              layout: 'horizontal',
                              label: 'pay',
                              height: 45
                            }}
                          />
                        </div>

                        {/* Credit/Debit Card Button */}
                        <div className="button-wrapper card-wrapper">
                          <PayPalButtons
                            fundingSource="card"
                            createSubscription={(data, actions) => {
                              return actions.subscription.create({
                                plan_id: paypalConfig.planId
                              });
                            }}
                            onApprove={handleApprove}
                            onError={(err) => console.error('[PayPal] Card Error:', err)}
                            style={{
                              shape: 'rect',
                              color: 'black',
                              layout: 'horizontal',
                              label: 'pay',
                              height: 45
                            }}
                          />
                        </div>

                        <div className="payment-separator secondary">
                          <span>{t('auth.or')}</span>
                        </div>

                        {/* PayPal Button */}
                        <div className="button-wrapper paypal-wrapper">
                          <PayPalButtons
                            fundingSource="paypal"
                            createSubscription={(data, actions) => {
                              return actions.subscription.create({
                                plan_id: paypalConfig.planId
                              });
                            }}
                            onApprove={handleApprove}
                            onError={(err) => console.error('[PayPal] PayPal Error:', err)}
                            style={{
                              shape: 'rect',
                              color: 'blue',
                              layout: 'horizontal',
                              label: 'subscribe',
                              height: 45,
                              tagline: false
                            }}
                          />
                        </div>
                      </div>

                      {/* Botão de Saltar (Modo Gratuito) */}
                      <div className="skip-container" style={{ marginTop: '20px', textAlign: 'center' }}>
                        <button
                          onClick={handleSkip}
                          className="skip-button"
                          style={{
                            background: 'transparent',
                            border: '1px solid #ccc',
                            padding: '10px 20px',
                            cursor: 'pointer',
                            color: '#666',
                            borderRadius: '4px',
                            fontSize: '14px'
                          }}
                        >
                          {t('pages.pricing.skipButton')}
                        </button>
                      </div>

                    </div>
                  </div>
                </PayPalScriptProvider>
              )
            )
          )
        )}
      </div>
    </div>
  );
};

export default Preco;
